import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/Skeleton";
import { Panel, Quiet } from "@/components/home/panel";
import {
  BADGE_META,
  BadgeShelf,
  HistoryPanel,
  ScoreBreakdown,
  type Badge,
  type HistoryRow,
  type ScoreLine,
} from "@/components/Leaderboard";
import {
  TierChip,
  TierEmblem,
  TierLadder,
  TierProgress,
  tierLook,
  type TierStep,
} from "@/components/tiers";

interface BoardRow {
  rank: number;
  user_id: string;
  full_name: string | null;
  email: string;
  score: number;
  listings: number;
  deals_done: number;
  tier: string;
  is_me: boolean;
}
interface MyStanding {
  rank: number;
  of: number;
  score: number;
  components: ScoreLine[];
  behind_next: number | null;
  next_rank: number | null;
  per_active_day: number;
  days_active: number;
  listings: number;
  streak: number;
  fx_complete: boolean;
  tier: string;
  next_tier: string | null;
  to_next_tier: number | null;
}
interface Board {
  period_start: string;
  period_label: string;
  is_final: boolean;
  leaders: BoardRow[];
  full_board: BoardRow[] | null;
  me: MyStanding | null;
  goal_daily: number;
  my_badges: Badge[];
  my_history: HistoryRow[];
  tiers: TierStep[];
}
interface MonthOption {
  period_month: string;
  label: string;
  is_final: boolean;
}

const MEDAL = ["🥇", "🥈", "🥉"];
const TOTAL_BADGES = Object.keys(BADGE_META).length;

export function Leaderboard() {
  const [params, setParams] = useSearchParams();
  const month = params.get("month");
  const [months, setMonths] = useState<MonthOption[]>([]);
  const [data, setData] = useState<Board | null>(null);

  useEffect(() => {
    api<MonthOption[]>("/api/leaderboard/months").then(setMonths).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    setData(null);
    const qs = month ? `?month=${month}` : "";
    api<Board>(`/api/leaderboard${qs}`)
      .then((r) => alive && setData(r))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [month]);

  const earnedKinds = useMemo(
    () => new Set((data?.my_badges ?? []).map((b) => b.kind)).size,
    [data],
  );

  const howPoints = data && (
    <Panel title="How points work">
      <ul className="space-y-1 text-[11.5px] text-slate-500">
        <li>+1 per listing published</li>
        <li>+10 per day you hit the {data.goal_daily}/day goal</li>
        <li>+200 per deal closed</li>
        <li>+20 per ₾1,000 commission</li>
        <li className="text-amber-300/80">−2 per listing later taken down</li>
      </ul>
      <p className="mt-2 text-[11px] text-slate-500">
        Failed publishes don't cost points — almost all of them are owners
        deleting their own listing or a portal being down.
      </p>
    </Panel>
  );

  // Only the first three get a medal on this page, however many leaders the
  // API returns for Home's panel.
  const podium = useMemo(() => (data?.leaders ?? []).slice(0, 3), [data]);
  const podiumIds = useMemo(() => new Set(podium.map((p) => p.user_id)), [podium]);

  return (
    <div className="w-full max-w-[1100px] mx-auto">
      <PageHeader
        title="Leaderboard"
        subtitle="Points from what you publish, what you close, and how clean the work is."
      />

      {months.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {months.map((m) => {
            const active = month ? month === m.period_month : m.period_month === months[0].period_month;
            return (
              <button
                key={m.period_month}
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.set("month", m.period_month);
                  setParams(next, { replace: true });
                }}
                className={`rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium transition ${
                  active
                    ? "bg-accent-500/15 text-accent-200 border border-accent-500/30"
                    : "text-slate-400 hover:text-slate-200 border border-white/5"
                }`}
              >
                {m.label}
                <span className="ml-1.5 text-[10px] text-slate-500">
                  {m.is_final ? "final" : "live"}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!data ? (
        <div className="space-y-3">
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      ) : (
        <div className="space-y-3">
          {/* podium */}
          <div className="grid gap-3 sm:grid-cols-3">
            {podium.map((p, i) => (
              <div
                key={p.user_id}
                className={`card px-4 py-3 flex items-center gap-3 ${
                  p.is_me ? "ring-1 ring-accent-500/30" : ""
                } ${i === 0 ? "bg-gradient-to-br from-amber-500/[0.08] via-transparent to-transparent" : ""}`}
              >
                <span className="text-2xl leading-none">{MEDAL[i]}</span>
                <Avatar name={p.full_name || p.email} size="sm" tier={p.tier} />
                <div className="min-w-0">
                  <div className="text-[13px] text-slate-200 truncate">
                    {p.is_me ? "You" : p.full_name || p.email}
                  </div>
                  <div className="text-lg font-semibold tabular-nums leading-none text-slate-100 mt-0.5">
                    {p.score.toLocaleString()}
                    <span className="text-[11px] text-slate-500 font-normal ml-1">pts</span>
                  </div>
                </div>
                {/* The tier gets the artwork rather than a chip: the podium
                    is the one row wide enough to carry it, and the medal to
                    its left already spends the row's small-glyph budget. */}
                <div className="ml-auto flex-shrink-0 flex flex-col items-center gap-0.5">
                  <TierEmblem tier={p.tier} px={40} />
                  <span className={`text-[10px] leading-none ${tierLook(p.tier).text}`}>
                    {tierLook(p.tier).label}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="grid gap-3 lg:grid-cols-3 items-start">
            <div className="lg:col-span-2 min-w-0 space-y-3">
              {/* full table where allowed, else your own position */}
              {data.full_board ? (
                <Panel title={`${data.period_label} · everyone`} flush>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12.5px]">
                      <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                        <tr>
                          <th className="text-left font-medium px-4 py-1.5">#</th>
                          <th className="text-left font-medium px-3 py-1.5">Broker</th>
                          <th className="text-left font-medium px-3 py-1.5">Tier</th>
                          <th className="text-right font-medium px-3 py-1.5">Points</th>
                          <th className="text-right font-medium px-3 py-1.5">Listings</th>
                          <th className="text-right font-medium px-3 py-1.5">Deals</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.full_board.map((r) => (
                          <tr
                            key={r.user_id}
                            className={`border-b border-white/[0.04] last:border-0 ${
                              r.is_me ? "bg-accent-500/[0.06]" : ""
                            }`}
                          >
                            <td className="px-4 py-1.5 tabular-nums text-slate-500">
                              {podiumIds.has(r.user_id) ? MEDAL[r.rank - 1] : r.rank}
                            </td>
                            <td className="px-3 py-1.5">
                              <span className="flex items-center gap-2 min-w-0">
                                <Avatar name={r.full_name || r.email} size="xs" tier={r.tier} />
                                <span className="text-slate-200 truncate max-w-[190px]">
                                  {r.is_me ? "You" : r.full_name || r.email}
                                </span>
                              </span>
                            </td>
                            <td className="px-3 py-1.5">
                              <TierChip tier={r.tier} size="xs" />
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-100 font-medium">
                              {r.score.toLocaleString()}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">
                              {r.listings.toLocaleString()}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">
                              {r.deals_done || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              ) : null}

              {data.me ? (
                <ScoreBreakdown
                  me={data.me}
                  periodLabel={data.period_label}
                  goalDaily={data.goal_daily}
                />
              ) : (
                <Panel title="Your score">
                  <Quiet
                    title="You're not ranked"
                    hint="Only brokers carrying a publishing goal appear on the board."
                  />
                </Panel>
              )}

              {data.my_history.length > 0 && <HistoryPanel rows={data.my_history} />}

              {!data.full_board && howPoints}
            </div>

            <div className="space-y-3 min-w-0">
              {data.me && (
                <Panel title="Your tier">
                  <TierProgress
                    tier={data.me.tier}
                    score={data.me.score}
                    steps={data.tiers}
                    toNext={data.me.to_next_tier}
                    nextTier={data.me.next_tier}
                  />
                </Panel>
              )}

              <Panel title="The ladder" flush>
                <TierLadder steps={data.tiers} tier={data.me?.tier ?? ""} />
              </Panel>

              <Panel
                title={`Your badges · ${earnedKinds}`}
                right={<span className="text-slate-500">of {TOTAL_BADGES}</span>}
                flush
              >
                <BadgeShelf badges={data.my_badges} showUnearned />
              </Panel>

              {data.full_board ? howPoints : null}

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
